import { App, Avatar, Button, Space, Tag, Upload } from 'antd';
import { CameraOutlined, DeleteOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import type { FileCategory } from '@/api/files';
import { fileUrl } from '@/shared/lib/file-url';

interface Props {
  /** Currently committed path (e.g. "student-photos/abc.jpg") or null. AntD Form passes this. */
  value?: string | null;
  /** Called only when the user *removes* the existing photo (null) — clears the form field. */
  onChange?: (path: string | null) => void;
  /**
   * Called when the user picks a new file (with the File) or clears a pending pick (null).
   * The form is responsible for uploading it on submit — see `usePhotoUpload` hook.
   */
  onPendingChange?: (file: File | null) => void;
  /** Backend file category — only used for client-side MIME guidance; the actual category lives in usePhotoUpload. */
  category: FileCategory;
  shape?: 'circle' | 'square';
  size?: number;
  allowRemove?: boolean;
  fallback?: React.ReactNode;
  maxSizeMb?: number;
}

const ACCEPT = 'image/jpeg,image/png,image/webp';

export function PhotoUpload({
  value,
  onChange,
  onPendingChange,
  shape = 'circle',
  size = 96,
  allowRemove = true,
  fallback,
  maxSizeMb = 5,
}: Props) {
  const { message } = App.useApp();
  const [pending, setPending] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);

  // Revoke the blob URL when it's replaced or unmounted to avoid leaks
  useEffect(() => {
    if (!pendingPreview) return;
    return () => URL.revokeObjectURL(pendingPreview);
  }, [pendingPreview]);

  const handleBefore = (file: File) => {
    if (!ACCEPT.split(',').includes(file.type)) {
      message.error('Only JPG, PNG, or WebP images are allowed.');
      return Upload.LIST_IGNORE;
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      message.error(`Image must be ${maxSizeMb} MB or smaller.`);
      return Upload.LIST_IGNORE;
    }
    setPending(file);
    setPendingPreview(URL.createObjectURL(file));
    onPendingChange?.(file);
    return Upload.LIST_IGNORE;
  };

  const handleRemove = () => {
    setPending(null);
    setPendingPreview(null);
    onPendingChange?.(null);
    onChange?.(null);
  };

  const displayUrl = pendingPreview ?? fileUrl(value);
  const fallbackNode = fallback ?? (shape === 'circle' ? <UserOutlined /> : <CameraOutlined />);
  const hasAnything = pending != null || (value != null && value !== '');

  return (
    <Space align="center" size={14}>
      <Avatar
        shape={shape}
        size={size}
        src={displayUrl ?? undefined}
        icon={!displayUrl ? fallbackNode : undefined}
        style={{ background: displayUrl ? undefined : '#e6e6e6', color: '#888' }}
      />
      <Space orientation="vertical" size={4}>
        <Space size={4}>
          <Upload accept={ACCEPT} showUploadList={false} beforeUpload={handleBefore}>
            <Button icon={<UploadOutlined />} size="small">
              {hasAnything ? 'Replace' : 'Upload'}
            </Button>
          </Upload>
          {hasAnything && allowRemove && (
            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={handleRemove}>
              Remove
            </Button>
          )}
        </Space>
        {pending && (
          <Tag color="warning" style={{ fontSize: 11 }}>
            Pending — uploaded when you save
          </Tag>
        )}
      </Space>
    </Space>
  );
}
